<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main" aria-label="My students">
    <!-- Header toolbar -->
    <mat-toolbar class="cd-header-toolbar">
      <div class="cd-header-left">
        <h1 class="cd-title">My Students</h1>
        <span class="cd-subtitle">
          <mat-icon class="ms-ferpa-icon" aria-hidden="true">shield</mat-icon>
          FERPA &amp; COPPA protected · Role-gated access
        </span>
      </div>

      <span class="cd-toolbar-spacer" aria-hidden="true"></span>

      <mat-button-toggle-group
        class="ms-view-toggle"
        [value]="view()"
        (change)="setView($event.value)"
        aria-label="Switch between students and groups view"
      >
        <mat-button-toggle value="students">
          <mat-icon aria-hidden="true">groups</mat-icon>
          Students
        </mat-button-toggle>
        <mat-button-toggle value="groups" *hasPermission="permissions.GROUPS.VIEW">
          <mat-icon aria-hidden="true">diversity_3</mat-icon>
          Groups
        </mat-button-toggle>
      </mat-button-toggle-group>

      @if (view() === 'students') {
        <mat-form-field appearance="outline" class="ms-search-field">
          <mat-label>Search by name or career</mat-label>
          <mat-icon matPrefix aria-hidden="true">search</mat-icon>
          <input
            matInput
            (input)="applySearch($event)"
            placeholder="e.g. Anna, Software Engineering"
            aria-label="Search students by name or career"
            autocomplete="off"
          />
          @if (searchValue()) {
            <button matSuffix mat-icon-button (click)="clearSearch()" aria-label="Clear search">
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </mat-form-field>
        <button
          *hasPermission="permissions.ASSIGNMENTS.MANAGE"
          mat-flat-button
          class="ms-assign-btn"
          routerLink="/counselors/students/assign"
          aria-label="Assign students to counselors"
        >
          <mat-icon aria-hidden="true">assignment_ind</mat-icon>
          Faculty Assignments
        </button>
      } @else {
        <mat-form-field appearance="outline" class="ms-search-field">
          <mat-label>Search groups</mat-label>
          <mat-icon matPrefix aria-hidden="true">search</mat-icon>
          <input
            matInput
            (input)="applyGroupSearch($event)"
            placeholder="Search group name"
            aria-label="Search groups"
            autocomplete="off"
          />
        </mat-form-field>
        <button
          *hasPermission="permissions.GROUPS.CREATE"
          mat-flat-button
          class="ms-assign-btn"
          (click)="openCreateGroup()"
          aria-label="Create a new group"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          Create Group
        </button>
      }
    </mat-toolbar>
    <!-- Filter toolbar -->
    @if (view() === 'students') {
      <mat-toolbar class="ms-filters-toolbar" aria-label="Student filters">
        <mat-icon class="ms-filter-toolbar-icon" aria-hidden="true">filter_list</mat-icon>
        <span class="ms-filter-label" aria-hidden="true">Grade</span>
        <mat-chip-listbox aria-label="Filter by grade" class="ms-chip-listbox">
          @for (g of gradeOptions; track g.val) {
            <mat-chip-option [selected]="gradeFilter() === g.val" (click)="setGradeFilter(g.val)">
              {{ g.label }}
            </mat-chip-option>
          }
        </mat-chip-listbox>
        <mat-divider vertical class="ms-toolbar-divider" aria-hidden="true"></mat-divider>
        <span class="ms-filter-label" aria-hidden="true">Status</span>
        <mat-chip-listbox aria-label="Filter by status" class="ms-chip-listbox">
          @for (s of statusOptions; track s.val) {
            <mat-chip-option [selected]="statusFilter() === s.val" (click)="setStatusFilter(s.val)">
              {{ s.label }}
            </mat-chip-option>
          }
        </mat-chip-listbox>
      </mat-toolbar>

      <!-- Loading -->
      @if (loading()) {
        <div class="ms-loading" role="status" aria-live="polite" aria-label="Loading students">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      }

      <!-- Error -->
      @if (error()) {
        <mat-card class="ms-error-card" role="alert">
          <mat-card-content class="ms-error-content">
            <mat-icon color="warn" aria-hidden="true">error_outline</mat-icon>
            <span>{{ error() }}</span>
          </mat-card-content>
        </mat-card>
      }

      <!-- Board -->
      @if (!loading() && !error()) {
        <div class="ms-board">
          <!-- ALL STUDENTS -->
          <mat-card class="ms-note ms-note--blue">
            <mat-card-header class="ms-note-header">
              <mat-icon
                mat-card-avatar
                aria-hidden="true"
                class="ms-note-avatar-icon ms-note-avatar-icon--blue"
                >groups</mat-icon
              >
              <mat-card-title>All Students</mat-card-title>
              <mat-card-subtitle>{{ filteredStudents().length }} total</mat-card-subtitle>
            </mat-card-header>
            <mat-divider></mat-divider>
            <mat-card-content class="ms-note-body">
              @if (filteredStudents().length === 0) {
                <mat-list>
                  <mat-list-item class="ms-empty-item" role="status">
                    <mat-icon matListItemIcon aria-hidden="true">search_off</mat-icon>
                    <span matListItemTitle>No students match your filters.</span>
                  </mat-list-item>
                </mat-list>
              } @else {
                <mat-nav-list class="ms-student-list" aria-label="All students">
                  @for (s of filteredStudents(); track s.id) {
                    <mat-list-item
                      class="ms-student-item"
                      (click)="openDetail(s)"
                      [attr.aria-label]="
                        'View details for ' +
                        (s.user.preferred_name || s.user.first_name) +
                        ' ' +
                        s.user.last_name +
                        ', ' +
                        gradeLabel(s.grade_level) +
                        (s.Profile?.Career?.name ? ', ' + s.Profile!.Career!.name : '')
                      "
                    >
                      <div
                        matListItemIcon
                        aria-hidden="true"
                        class="cd-student-avatar {{ avatarClass(s) }}"
                      >
                        <app-user-avatar
                          [imageUrl]="s.Profile?.profile_picture_path"
                          [name]="s.user.first_name + ' ' + s.user.last_name"
                          [color]="avatarClass(s)"
                        />
                      </div>
                      <span matListItemTitle
                        >{{ s.user.preferred_name || s.user.first_name }}
                        {{ s.user.last_name }}</span
                      >
                      <span matListItemLine>
                        {{ gradeLabel(s.grade_level) }}
                        @if (s.Profile?.Career?.name) {
                          · {{ s.Profile!.Career!.name }}
                        }
                      </span>
                    </mat-list-item>
                    <mat-divider inset></mat-divider>
                  }
                </mat-nav-list>
              }
            </mat-card-content>
            <mat-divider></mat-divider>
            <mat-card-footer class="ms-note-footer">
              <mat-icon aria-hidden="true">info_outline</mat-icon>
              <span aria-live="polite"
                >{{ filteredStudents().length }} student{{
                  filteredStudents().length !== 1 ? 's' : ''
                }}</span
              >
            </mat-card-footer>
          </mat-card>

          <!-- NEEDS ATTENTION -->
          <mat-card class="ms-note ms-note--orange">
            <mat-card-header class="ms-note-header">
              <mat-icon
                mat-card-avatar
                aria-hidden="true"
                class="ms-note-avatar-icon ms-note-avatar-icon--orange"
                >warning_amber</mat-icon
              >
              <mat-card-title>Needs Attention</mat-card-title>
              <mat-card-subtitle>{{ needsAttentionStudents().length }} flagged</mat-card-subtitle>
            </mat-card-header>
            <mat-divider></mat-divider>
            <mat-card-content class="ms-note-body">
              @if (needsAttentionStudents().length === 0) {
                <mat-list>
                  <mat-list-item class="ms-empty-item ms-empty-item--good" role="status">
                    <mat-icon matListItemIcon aria-hidden="true" class="ms-good-icon"
                      >check_circle_outline</mat-icon
                    >
                    <span matListItemTitle>No students need attention right now</span>
                  </mat-list-item>
                </mat-list>
              } @else {
                <mat-nav-list class="ms-student-list" aria-label="Students needing attention">
                  @for (s of needsAttentionStudents(); track s.id) {
                    <mat-list-item
                      class="ms-student-item ms-student-item--warn"
                      (click)="openDetail(s)"
                      [attr.aria-label]="
                        'View details for ' +
                        (s.user.preferred_name || s.user.first_name) +
                        ' ' +
                        s.user.last_name +
                        ', ' +
                        gradeLabel(s.grade_level) +
                        (s.Profile?.Career?.name ? ', ' + s.Profile!.Career!.name : '') +
                        ', profile ' +
                        (s.Profile?.profile_complete_percent ?? 0) +
                        '% complete, needs attention'
                      "
                    >
                      <div
                        matListItemIcon
                        aria-hidden="true"
                        class="cd-student-avatar {{ avatarClass(s) }}"
                      >
                        <app-user-avatar
                          [imageUrl]="s.Profile?.profile_picture_path"
                          [name]="s.user.first_name + ' ' + s.user.last_name"
                          [color]="avatarClass(s)"
                        />
                      </div>
                      <span matListItemTitle
                        >{{ s.user.preferred_name || s.user.first_name }}
                        {{ s.user.last_name }}</span
                      >
                      <span matListItemLine>
                        {{ gradeLabel(s.grade_level) }}
                        @if (s.Profile?.Career?.name) {
                          · {{ s.Profile!.Career!.name }}
                        }
                      </span>
                      <div matListItemMeta class="ms-item-meta-right">
                        <mat-progress-bar
                          mode="determinate"
                          [value]="s.Profile?.profile_complete_percent ?? 0"
                          color="warn"
                          [attr.aria-label]="
                            (s.Profile?.profile_complete_percent ?? 0) + '% profile complete'
                          "
                          [attr.aria-valuenow]="s.Profile?.profile_complete_percent ?? 0"
                          aria-valuemin="0"
                          aria-valuemax="100"
                          class="ms-item-bar"
                        >
                        </mat-progress-bar>
                        <mat-chip-set aria-hidden="true">
                          <mat-chip disableRipple class="ms-status-chip chip-needs-attn"
                            >Needs attention</mat-chip
                          >
                        </mat-chip-set>
                      </div>
                    </mat-list-item>
                    <mat-divider inset></mat-divider>
                  }
                </mat-nav-list>
              }
            </mat-card-content>
            <mat-divider></mat-divider>
            <mat-card-footer class="ms-note-footer ms-note-footer--warn">
              <mat-icon aria-hidden="true">flag</mat-icon>
              <span aria-live="polite"
                >{{ needsAttentionStudents().length }} need{{
                  needsAttentionStudents().length !== 1 ? '' : 's'
                }}
                follow-up</span
              >
            </mat-card-footer>
          </mat-card>
        </div>
      }
    }
    @if (view() === 'groups') {
      @if (groupsLoading()) {
        <div class="ms-loading" role="status" aria-live="polite" aria-label="Loading groups">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      }

      @if (groupsError()) {
        <mat-card class="ms-error-card" role="alert">
          <mat-card-content class="ms-error-content">
            <mat-icon color="warn" aria-hidden="true">error_outline</mat-icon>
            <span>{{ groupsError() }}</span>
          </mat-card-content>
        </mat-card>
      }

      @if (!groupsLoading() && !groupsError()) {
        @if (hasSelection()) {
          <mat-toolbar class="gr-bulk-toolbar">
            <span>{{ selectedGroupIds().size }} selected</span>
            <span class="cd-toolbar-spacer" aria-hidden="true"></span>
            <button mat-button color="warn" (click)="deleteSelected()">
              <mat-icon aria-hidden="true">delete</mat-icon>
              Delete
            </button>
            <button mat-icon-button (click)="clearSelection()" aria-label="Clear selection">
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          </mat-toolbar>
        }
        <div class="gr-board">
          @if (filteredGroups().length === 0) {
            <mat-card class="ms-note">
              <mat-card-content class="ms-empty-item" role="status">
                <mat-icon aria-hidden="true">group_off</mat-icon>
                <span matListItemTitle>No groups match your search.</span>
              </mat-card-content>
            </mat-card>
          } @else {
            @for (g of filteredGroups(); track g.id) {
              <mat-card
                class="gr-card"
                (click)="openGroupDetail(g)"
                role="button"
                tabindex="0"
                [attr.aria-label]="'View details for ' + g.name"
                (keydown.enter)="openGroupDetail(g)"
              >
                <mat-card-content class="gr-card-content">
                  <mat-checkbox
                    class="gr-card-checkbox"
                    [checked]="isSelected(g.id)"
                    (click)="toggleGroupSelection(g.id, $event)"
                    [attr.aria-label]="'Select ' + g.name"
                  ></mat-checkbox>

                  <div class="gr-avatar" aria-hidden="true">
                    <mat-icon>{{ g.icon ? g.icon : 'groups' }}</mat-icon>
                  </div>
                  <div class="gr-card-body">
                    <div class="gr-card-title-row">
                      <span class="gr-card-name">{{ g.name }}</span>
                    </div>
                    <p class="gr-card-desc">{{ g.description }}</p>
                    <span class="gr-card-meta">
                      {{ g.members.length }} member{{ g.members.length !== 1 ? 's' : '' }}
                    </span>
                  </div>

                  <button
                    mat-icon-button
                    class="gr-card-edit"
                    (click)="openEditGroup(g, $event)"
                    aria-label="Edit group details"
                  >
                    <mat-icon aria-hidden="true">edit</mat-icon>
                  </button>
                </mat-card-content>
              </mat-card>
            }
          }
        </div>
      }
    }

    <!-- FERPA toolbar -->
    <mat-toolbar class="ms-ferpa-toolbar">
      <mat-icon aria-hidden="true">shield</mat-icon>
      <span class="ms-ferpa-text">
        Student records are protected under
        <strong>FERPA (20 U.S.C. § 1232g)</strong> and <strong>COPPA (15 U.S.C. § 6501)</strong>.
        Access is limited to authorized school officials with a legitimate educational interest. Do
        not share, screenshot, or export student data outside of approved systems.
      </span>
    </mat-toolbar>
  </main>
</div>

<!-- DETAIL PANEL -->
@if (selectedStudent()) {
  <div
    class="ms-detail-backdrop"
    (click)="closeDetail()"
    role="button"
    tabindex="0"
    aria-label="Close student detail panel"
    (keydown.enter)="closeDetail()"
    (keydown.escape)="closeDetail()"
  ></div>

  <div
    class="ms-detail-sidenav"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="
      'Student details for ' +
      (selectedStudent()!.user.preferred_name || selectedStudent()!.user.first_name) +
      ' ' +
      selectedStudent()!.user.last_name
    "
  >
    <!-- Header toolbar -->
    <mat-toolbar class="ms-detail-toolbar">
      <div
        class="cd-student-avatar ms-detail-avatar {{ avatarClass(selectedStudent()!) }}"
        aria-hidden="true"
      >
        <app-user-avatar
          [imageUrl]="selectedStudent()!.Profile?.profile_picture_path"
          [name]="selectedStudent()!.user.first_name + ' ' + selectedStudent()!.user.last_name"
          [color]="avatarClass(selectedStudent()!)"
        />
      </div>
      <div class="ms-detail-title-block">
        <span class="ms-detail-name">
          {{ selectedStudent()!.user.preferred_name || selectedStudent()!.user.first_name }}
          {{ selectedStudent()!.user.last_name }}
        </span>
        <span class="ms-detail-sub">
          {{ gradeLabel(selectedStudent()!.grade_level) }}
          @if (selectedStudent()!.Profile?.Career?.name) {
            &nbsp;·&nbsp;{{ selectedStudent()!.Profile!.Career!.name }}
          }
        </span>
      </div>
      <span class="cd-toolbar-spacer" aria-hidden="true"></span>
      <button mat-icon-button (click)="closeDetail()" aria-label="Close student detail panel">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </mat-toolbar>

    <mat-divider></mat-divider>

    <!-- Info list -->
    <mat-list class="ms-detail-list" aria-label="Student information">
      <mat-list-item>
        <mat-icon matListItemIcon aria-hidden="true">email</mat-icon>
        <div matListItemTitle>School email</div>
        <div matListItemLine>{{ selectedStudent()!.user.email }}</div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      <mat-list-item>
        <mat-icon matListItemIcon aria-hidden="true">school</mat-icon>
        <div matListItemTitle>Grade level</div>
        <div matListItemLine>{{ gradeLabel(selectedStudent()!.grade_level) }}</div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      <mat-list-item>
        <mat-icon matListItemIcon aria-hidden="true">work</mat-icon>
        <div matListItemTitle>Career interest</div>
        <div matListItemLine>{{ selectedStudent()!.Profile?.Career?.name ?? 'Not set' }}</div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      <mat-list-item>
        <mat-icon matListItemIcon aria-hidden="true">location_city</mat-icon>
        <div matListItemTitle>School</div>
        <div matListItemLine>{{ selectedStudent()!.School?.name ?? '—' }}</div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      <mat-list-item>
        <mat-icon matListItemIcon aria-hidden="true">person</mat-icon>
        <div matListItemTitle>Account status</div>
        <div matListItemLine>{{ selectedStudent()!.user.is_active ? 'Active' : 'Inactive' }}</div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      <mat-list-item class="ms-completion-item">
        <mat-icon matListItemIcon aria-hidden="true">bar_chart</mat-icon>
        <div matListItemTitle>
          Profile completion
          <span class="ms-detail-pct" aria-hidden="true"
            >{{ selectedStudent()!.Profile?.profile_complete_percent ?? 0 }}%</span
          >
        </div>
        <div matListItemLine>
          <mat-progress-bar
            mode="determinate"
            [value]="selectedStudent()!.Profile?.profile_complete_percent ?? 0"
            [color]="completionColor(selectedStudent()!.Profile?.profile_complete_percent ?? 0)"
            [attr.aria-label]="
              (selectedStudent()!.Profile?.profile_complete_percent ?? 0) + '% profile complete'
            "
            [attr.aria-valuenow]="selectedStudent()!.Profile?.profile_complete_percent ?? 0"
            aria-valuemin="0"
            aria-valuemax="100"
            class="ms-detail-bar"
          >
          </mat-progress-bar>
        </div>
      </mat-list-item>
      <mat-divider inset></mat-divider>

      @if (selectedStudent()!.Profile?.bio) {
        <mat-list-item class="ms-bio-item">
          <mat-icon matListItemIcon aria-hidden="true">notes</mat-icon>
          <div matListItemTitle>Bio</div>
          <div matListItemLine class="ms-bio-line">{{ selectedStudent()!.Profile!.bio }}</div>
        </mat-list-item>
        <mat-divider inset></mat-divider>
      }
    </mat-list>

    <!-- FERPA footer -->
    <mat-toolbar class="ms-detail-ferpa-toolbar">
      <mat-icon class="ms-ferpa-icon" aria-hidden="true">shield</mat-icon>
      FERPA-protected record · Authorized counselor access only
    </mat-toolbar>
  </div>
}
